import {useCallback} from 'react';
import {sendToStatist} from 'packages/statist';

import {FormatBoldNewIcon} from '@time-webkit/all/icons/format-bold-new';
import {FormatItalicNewIcon} from '@time-webkit/all/icons/format-italic-new';
import {FormatStrikeNewIcon} from '@time-webkit/all/icons/format-strike-new';
import {CodeBlockIcon} from '@time-webkit/all/icons/code-block';
import {FormatQuoteOpenNewIcon} from '@time-webkit/all/icons/format-quote-open-new';
import {FormatListBulletedNewIcon} from '@time-webkit/all/icons/format-list-bulleted-new';
import {FormatListNumberedNewIcon} from '@time-webkit/all/icons/format-list-numbered-new';

import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import {Divider} from '../divider';
import {ActionButton} from '../action-button';
import {LinkPopover} from '../link-popovers';
import {useEditorContext} from '../../context';
import {ShortcutTooltip} from '../shortcut-tooltip';
import {HeadingsComponent} from '../headings';
import {OverflowContainer} from '../overflow-container';

import {getHeadingLevelActive} from '../../extensions/heading/heading-extension';

import styles from './styles.module.css';

interface FormattingBarProps {
    outerRef?: React.RefObject<HTMLDivElement>;
    onClosePopover?: (event: MouseEvent) => void;
}

export const FormattingBar = ({outerRef, onClosePopover}: FormattingBarProps) => {
    const {editor, features} = useEditorContext();
    const {codeBlock} = features;

    const handleBoldClick = useCallback(() => {
        editor?.chain().focus().toggleMark('bold').run();
        sendToStatist('editor.format.tap', {formatType: 'bold', hotkey: false});
    }, [editor]);
    const handleItalicClick = useCallback(() => {
        editor?.chain().focus().toggleMark('italic').run();
        sendToStatist('editor.format.tap', {formatType: 'italic', hotkey: false});
    }, [editor]);
    const handleStrikeClick = useCallback(() => {
        editor?.chain().focus().toggleMark('strike').run();
        sendToStatist('editor.format.tap', {formatType: 'strikethrough', hotkey: false});
    }, [editor]);
    const handleCodeBlockClick = useCallback(() => {
        editor?.chain().focus().toggleCodeBlock().run();
        sendToStatist('editor.format.tap', {formatType: 'code_block', hotkey: false});
    }, [editor]);
    const handleQuoteClick = useCallback(() => {
        editor?.chain().focus().toggleBlockquote().run();
        sendToStatist('editor.format.tap', {formatType: 'quote', hotkey: false});
    }, [editor]);
    const handleOrderedListClick = useCallback(() => {
        editor?.chain().focus().toggleOrderedList().run();
        sendToStatist('editor.format.tap', {formatType: 'list_ordered', hotkey: false});
    }, [editor]);
    const handleBulletListClick = useCallback(() => {
        editor?.chain().focus().toggleBulletList().run();
        sendToStatist('editor.format.tap', {formatType: 'list_bulleted', hotkey: false});
    }, [editor]);

    const isCodeBlockActive = editor?.isActive('codeBlock');
    const isTableActive = editor?.isActive('table');
    const headingLevelActive = getHeadingLevelActive(editor);
    const isMentionActive = editor?.isActive('mention');

    return (
        <OverflowContainer
            popoverTrigger={outerRef}
            placement='top right'
            className={styles.bar}
            data-testid='editor_formatting_bar'
            onClose={onClosePopover}
        >
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownBold} isHidden={isCodeBlockActive}>
                <ActionButton
                    aria-label='Bold'
                    isActive={editor?.isActive('bold')}
                    isDisabled={isCodeBlockActive || Boolean(headingLevelActive)}
                    onPress={handleBoldClick}
                    icon={<FormatBoldNewIcon />}
                    data-qa='editor_button_bold'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownItalic} isHidden={isCodeBlockActive}>
                <ActionButton
                    aria-label='Italic'
                    isActive={editor?.isActive('italic')}
                    isDisabled={isCodeBlockActive}
                    onPress={handleItalicClick}
                    icon={<FormatItalicNewIcon />}
                    data-qa='editor_button_italic'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownStrike} isHidden={isCodeBlockActive}>
                <ActionButton
                    aria-label='Strike'
                    isActive={editor?.isActive('strike')}
                    isDisabled={isCodeBlockActive}
                    onPress={handleStrikeClick}
                    icon={<FormatStrikeNewIcon />}
                    data-qa='editor_button_strike'
                />
            </ShortcutTooltip>
            <HeadingsComponent editor={editor} disabled={isCodeBlockActive || isTableActive} />
            <Divider />
            <LinkPopover editor={editor} disabled={isCodeBlockActive || isMentionActive} />
            {codeBlock && <Divider />}
            {codeBlock && <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownCodeBlock}>
                <ActionButton
                    aria-label='Code Block'
                    isActive={editor?.isActive('codeBlock')}
                    isDisabled={isTableActive}
                    onPress={handleCodeBlockClick}
                    icon={<CodeBlockIcon />}
                    data-qa='editor_button_codeBlock'
                />
            </ShortcutTooltip>}
            <Divider />
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownOrderedList}>
                <ActionButton
                    aria-label='Ordered List'
                    isActive={editor?.isActive('orderedList')}
                    isDisabled={isTableActive}
                    onPress={handleOrderedListClick}
                    icon={<FormatListNumberedNewIcon />}
                    data-qa='editor_button_orederedList'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownBulletList}>
                <ActionButton
                    aria-label='Bullet List'
                    isActive={editor?.isActive('bulletList')}
                    isDisabled={isTableActive}
                    onPress={handleBulletListClick}
                    icon={<FormatListBulletedNewIcon />}
                    data-qa='editor_button_bulletList'
                />
            </ShortcutTooltip>
            <Divider />
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownQuote}>
                <ActionButton
                    aria-label='Block Quote'
                    isActive={editor?.isActive('blockquote')}
                    isDisabled={isTableActive}
                    onPress={handleQuoteClick}
                    icon={<FormatQuoteOpenNewIcon />}
                    data-qa='editor_button_blockquote'
                />
            </ShortcutTooltip>
        </OverflowContainer>
    );
};
